import {Component, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import * as _ from 'lodash-es';
import {Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {AutocompleteListComponent, DisplayVariant} from '../../autocomplete/autocomplete-list.component';


interface AutocompleteHighlightedItem {
    address: SafeHtml;
}

interface DisplayWalletAddressVariant extends DisplayVariant<string> {
    variant: string;
    highlighted: AutocompleteHighlightedItem;
}

@Component({
    selector:    'ul[dexWalletAutocompleteList]',
    templateUrl: './dex-wallet-autocomplete-list.component.html',
    styleUrls:   ['./dex-wallet-autocomplete-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone:  false
})
export class DexWalletAutocompleteListComponent
    extends AutocompleteListComponent<string, DisplayWalletAddressVariant>
    implements OnInit {

    @Input()
    public term$: Subject<string>;

    private _autocompleteItems: string[];

    private readonly domSanitizer = this.injector.get(DomSanitizer);

    private term: string;

    public override ngOnInit(): void {
        super.ngOnInit();

        this.listenToTerm();
    }

    public override setVariants(variants: string[], autocompleteItems?: string[]): void {
        if (this._variants !== variants || this._autocompleteItems !== autocompleteItems) {
            this._variants = variants;
            this._autocompleteItems = autocompleteItems;
            this.updateVariants();
        }
    }

    protected override updateVariants(): void {
        this.displayVariants = this._autocompleteItems.map<DisplayWalletAddressVariant>(
            item => this.createDisplayVariant(item)
        );

        this.selectedVariant = _.head(this._variants);

        if (this.term) {
            this.updateDisplayVariantsByTerm(this.term);
        }

        this.updateIsEmpty();
    }

    protected createDisplayVariant(item: string): DisplayWalletAddressVariant {
        return {
            variant: item,

            highlighted: !this.term
                         ? this.createHighlightedItem(item, '') // TODO
                         : null /** To be created by {@link updateDisplayVariantsByTerm} */
        };
    }

    private createHighlightedItem(address: string, term: string): AutocompleteHighlightedItem {
        const hAddress = this.getHighlightedItemPart(address, term);

        return {address: hAddress};
    }

    private getTermRegExp(term: string): RegExp {
        const termEscapedWords = term
            .split(/\s+/)
            .map(_.escapeRegExp);

        /**
         * [TRAD-195] Use start-with in security search on UI
         */
        const regExpStr = `\\b(${termEscapedWords.join('|')})`;

        return new RegExp(regExpStr, 'gi');
    }

    private highlightString(str: string, term: string): string {
        let resultStr = str;

        if (str && term) {
            const regExp = this.getTermRegExp(term);

            resultStr = str.replace(regExp, match => `<mark>${match}</mark>`);
        }

        return resultStr;
    }

    private getHighlightedItemPart(address: string, term: string): SafeHtml {
        const markupStr = this.highlightString(address, term);

        return this.domSanitizer.bypassSecurityTrustHtml(markupStr);
    }

    private listenToTerm(): void {
        this.term$.pipe(
            distinctUntilChanged(),
            tap(term => this.term = term),
            tap(term => this.updateDisplayVariantsByTerm(term)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private updateDisplayVariantsByTerm(term: string): void {
        let firstMatchedVariant: DisplayWalletAddressVariant;
        let isSelectedVariantMatched: boolean = true;

        this.displayVariants.forEach(displayVariant => {
            const searchTerm = this.isItemMatchedBySearchTerms(displayVariant.variant, term)
                ? term
                : '';

            displayVariant.highlighted = this.createHighlightedItem(
                displayVariant.variant,
                searchTerm
            );

            if (searchTerm) {
                firstMatchedVariant = firstMatchedVariant || displayVariant;
            } else {
                isSelectedVariantMatched = this.selectedVariant === displayVariant.variant
                    ? false
                    : isSelectedVariantMatched;
            }
        });

        if (!isSelectedVariantMatched && firstMatchedVariant) {
            this.selectedVariant = firstMatchedVariant.variant;
        }
    }

    private isItemMatchedBySearchTerms(addr: string, term: string): boolean {
        const regExp = this.getTermRegExp(term);

        return regExp.test(addr);
    }

}
